/* eslint-disable @angular-eslint/no-host-metadata-property */
/* eslint-disable @typescript-eslint/naming-convention */
import { ConfigurableFocusTrapFactory, FocusTrap } from '@angular/cdk/a11y';
import {
   AfterViewInit,
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   ElementRef,
   OnDestroy,
   ViewChild,
   ViewEncapsulation,
} from '@angular/core';
import { ThemePalette } from '@angular/material/core';
import { MatCalendar } from '@angular/material/datepicker';
import { Subject } from 'rxjs';

import { LcsDateTimePickerComponent } from '../date-time-picker/date-time-picker.component';
import { lcsDateTimePickerAnimations } from './date-time-picker-animations';

/**
 * Component used as the content for the date time picker overlay. We use this instead of using
 */
@Component({
   // eslint-disable-next-line @angular-eslint/component-selector
   selector: 'lcs-date-time-picker-content',
   templateUrl: 'date-time-picker-content.component.html',
   styleUrls: ['date-time-picker-content.component.scss'],
   host: {
      'class': 'lcs-date-time-picker-content mat-datepicker-content',
      '[@transformPanel]': 'animationState',
      '(@transformPanel.done)': 'animationDone.next($event)',
   },
   animations: [lcsDateTimePickerAnimations.transformPanel, lcsDateTimePickerAnimations.fadeInCalendar],
   exportAs: 'lcsDateTimePickerContent',
   changeDetection: ChangeDetectionStrategy.OnPush,
   encapsulation: ViewEncapsulation.None,
   standalone: false,
})
export class LcsDateTimePickerContentComponent<D> implements AfterViewInit, OnDestroy {
   /** Reference to the internal calendar component. */
   @ViewChild(MatCalendar) calendar: MatCalendar<D>;

   /** Reference to the date time picker that created the overlay. */
   dateTimePicker: LcsDateTimePickerComponent<D>;

   /** Whether the date time picker is above or below the input. */
   isAbove: boolean;

   /** Current state of the animation. */
   animationState: 'enter' | 'void' = 'enter';

   /** Emits when an animation has finished. */
   animationDone = new Subject<AnimationEvent>();

   set color(value: ThemePalette) {
      const colorPalette = value;

      if (colorPalette !== this._color) {
         if (this._color) {
            this.elementRef.nativeElement.classList.remove(`mat-${this._color}`);
         }
         if (colorPalette) {
            this.elementRef.nativeElement.classList.add(`mat-${colorPalette}`);
         }

         this._color = colorPalette;
      }
   }

   get color(): ThemePalette {
      return this._color;
   }

   private _color: ThemePalette;

   /** Default color to fall back to if no value is set. */
   defaultColor: ThemePalette | undefined;

   private focusTrap: FocusTrap;

   constructor(
      private elementRef: ElementRef,
      private changeDetectorRef: ChangeDetectorRef,
      focusTrapFactory: ConfigurableFocusTrapFactory
   ) {
      this.focusTrap = focusTrapFactory.create(elementRef.nativeElement);
   }

   ngAfterViewInit() {
      this.calendar.focusActiveCell();
   }

   ngOnDestroy() {
      this.animationDone.complete();
      this.focusTrap.destroy();
   }

   startExitAnimation(): Subject<AnimationEvent> {
      this.animationState = 'void';
      this.changeDetectorRef.markForCheck();
      return this.animationDone;
   }
}
